import Autocomplete, { AutocompleteProps, createFilterOptions } from '@mui/material/Autocomplete';
import { ChipTypeMap } from '@mui/material/Chip';
import { ElementType, ReactNode, SyntheticEvent, useState } from 'react';
import TextField from '@mui/material/TextField';
import { useFieldContext } from '@/lib/form.ts';
import { CreateOptionItem } from '@/common/components/form-creatable-combobox/types.ts';
import {
  AutocompleteFreeSoloValueMapping,
  FilterOptionsState,
} from '@mui/material/useAutocomplete';
import {
  defaultGetOptionLabel,
  isCreateOption,
} from '@/common/components/form-creatable-combobox/utils.ts';
import { useLingui } from '@lingui/react/macro';
import { useStore } from '@tanstack/react-form';
import { useShouldShowFieldErrors } from '@/common/hooks/useShouldShowFieldErrors.ts';
import { FormFieldErrors } from '@/common/components/form-field-errors/FormFieldErrors.tsx';
import Box from '@mui/material/Box';
import { COMMON_VALIDATION_ERRORS, ZodIssueMap } from '@/common/form/utils.ts';
import CircularProgress from '@mui/material/CircularProgress';
import { useTheme } from '@mui/material/styles';

const zodIssueMap: ZodIssueMap = {
  invalidType: COMMON_VALIDATION_ERRORS.required,
  tooSmall: COMMON_VALIDATION_ERRORS.required, // It doesn't make sense to define .min() on select values other than to require it
};

export interface FormCreatableComboboxProps<
  Value,
  OptionKey extends string | number,
  ChipComponent extends ElementType = ChipTypeMap['defaultComponent'],
> extends Omit<
    AutocompleteProps<Value | CreateOptionItem, false, false, false, ChipComponent>,
    | 'renderInput'
    | 'options'
    | 'getOptionKey'
    | 'disableClearable'
    | 'value'
    | 'onChange'
    | 'filterOptions'
    | 'getOptionLabel'
  > {
  errorMessage?: string;
  label: ReactNode;
  required?: boolean;
  options: readonly Value[];
  getOptionKey: (option: Value) => OptionKey;
  getOptionLabel: (option: Value) => string;
  getCreateOptionLabel?: (inputValue: string) => string;
  placeholder?: string;
  onCreateNew?: (inputValue: string) => Promise<OptionKey>;
  canCreateNew?: boolean;
}

export const FormCreatableCombobox = <
  Value,
  OptionKey extends string | number,
  ChipComponent extends ElementType = ChipTypeMap['defaultComponent'],
>({
  errorMessage,
  label,
  required,
  options,
  fullWidth = true,
  size = 'small',
  getOptionKey,
  getOptionLabel,
  getCreateOptionLabel,
  placeholder,
  onCreateNew,
  loading,
  readOnly,
  canCreateNew = true,
  ...rest
}: FormCreatableComboboxProps<Value, OptionKey, ChipComponent>) => {
  const { t } = useLingui();
  const field = useFieldContext<OptionKey | null>();
  const { shouldShowFieldErrors, onBlur } = useShouldShowFieldErrors(!!errorMessage);
  const value = useStore(field.store, (state) => state.value);
  const filterOptions = createFilterOptions<Value | CreateOptionItem>();
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const theme = useTheme();

  const selectedOption = options.find((opt) => getOptionKey(opt) === value) ?? null;

  const optionsOrCreatingNew: readonly Value[] | CreateOptionItem[] = isCreatingNew
    ? [{ key: '@@CREATE', label: t`Creating...`, inputValue: '' }]
    : options;

  const wrappedGetOptionKey = (
    option: Value | CreateOptionItem | AutocompleteFreeSoloValueMapping<false>
  ) => {
    if (isCreateOption(option)) {
      return option.key;
    }

    return getOptionKey(option);
  };

  const wrappedGetOptionLabel = (
    option: Value | CreateOptionItem | AutocompleteFreeSoloValueMapping<false>
  ) => {
    if (isCreateOption(option)) {
      return option.label;
    }

    return getOptionLabel?.(option) ?? defaultGetOptionLabel(option);
  };

  const handleBlur = () => {
    field.handleBlur();
    onBlur();
  };

  const handleCreateNew = async (inputValue: string) => {
    if (!onCreateNew) {
      return;
    }

    try {
      setIsCreatingNew(true);
      field.handleChange(null);
      const newValue = await onCreateNew(inputValue);
      field.handleChange(newValue);
    } catch (_) {
      handleBlur(); // So the error message is shown
    } finally {
      setIsCreatingNew(false);
    }
  };

  const handleChange = (_: SyntheticEvent, newValue: Value | CreateOptionItem | null) => {
    if (isCreateOption(newValue)) {
      void handleCreateNew(newValue.inputValue);
      return;
    }

    field.handleChange(newValue ? getOptionKey(newValue) : null);
  };

  const handleFilterOptions = (
    options: (Value | CreateOptionItem)[],
    params: FilterOptionsState<Value | CreateOptionItem>
  ) => {
    const filtered = filterOptions(options, params);

    if (isCreatingNew || !canCreateNew) {
      return filtered;
    }

    const { inputValue } = params;
    // Suggest the creation of a new value
    const isExisting = filtered.some((opt) => wrappedGetOptionLabel(opt) === inputValue);
    if (inputValue !== '' && !isExisting) {
      filtered.push({
        key: '@@CREATE',
        inputValue,
        label: getCreateOptionLabel?.(inputValue) ?? t`Create "${inputValue}"`,
      });
    }

    return filtered;
  };

  return (
    <Autocomplete<Value | CreateOptionItem, false, false, false, ChipComponent>
      {...rest}
      disableClearable={false}
      fullWidth={fullWidth}
      size={size}
      loading={loading || isCreatingNew}
      options={optionsOrCreatingNew}
      onBlur={handleBlur}
      readOnly={readOnly || isCreatingNew}
      slotProps={{
        paper: {
          elevation: 8,
        },
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required={required}
          placeholder={
            placeholder ?? (canCreateNew ? t`Type to search or to create new` : t`Type to search`)
          }
          error={shouldShowFieldErrors}
          slotProps={{
            input: {
              ...params.InputProps,
              sx: {
                color: isCreatingNew ? theme.palette.text.secondary : undefined,
                fontStyle: isCreatingNew ? 'italic' : undefined,
              },
              endAdornment: (
                <>
                  {isCreatingNew ? (
                    <CircularProgress color="inherit" size={16} aria-label={t`Creating new item`} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            },
          }}
          helperText={
            shouldShowFieldErrors && (
              <>
                <FormFieldErrors zodIssueMap={zodIssueMap} />
                {errorMessage && (
                  <Box
                    data-testid={`${field.name}-error`}
                    component="span"
                    sx={{ display: 'block' }}
                  >
                    {errorMessage}
                  </Box>
                )}
              </>
            )
          }
        />
      )}
      getOptionKey={wrappedGetOptionKey}
      getOptionLabel={wrappedGetOptionLabel}
      value={isCreatingNew ? optionsOrCreatingNew[0] : selectedOption}
      onChange={handleChange}
      filterOptions={handleFilterOptions}
    />
  );
};
